"use client";
import { useEffect, useState } from "react";
import {
  ThemeColor,
  ThemeConfig,
  ThemeMode,
  ThemeProviderContext,
  ThemeProviderProps,
} from "./ThemeProviderContext/ThemeProviderContext";

export const availableColors: ThemeColor[] = [
  "default",
  "blue",
  "green",
  "rose",
  "violet",
  "orange",
  "yellow",
];

export const availableModes: ThemeMode[] = ["light", "dark"];

export function ThemeProvider({
  children,
  defaultTheme = { mode: "light", color: "default" },
  storageKey = "theme-config",
  attribute = "class",
  enableSystem = false,
  disableTransitionOnChange = false,
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<ThemeConfig>(() => {
    // Check if we're in browser environment
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          return JSON.parse(stored) as ThemeConfig;
        } catch {
          return defaultTheme;
        }
      }
    }
    return defaultTheme;
  });

  useEffect(() => {
    const root = window.document.documentElement;

    // Remove all existing theme classes
    availableModes.forEach((mode) => root.classList.remove(mode));
    availableColors.forEach((color) => root.classList.remove(color));

    // Apply current theme classes
    root.classList.add(theme.mode);
    if (theme.color !== "default") {
      root.classList.add(theme.color);
    }
  }, [theme]);

  const updateTheme = (newTheme: ThemeConfig) => {
    localStorage.setItem(storageKey, JSON.stringify(newTheme));
    setTheme(newTheme);
  };

  const setMode = (mode: ThemeMode) => {
    const newTheme = { ...theme, mode };
    updateTheme(newTheme);
  };

  const setColor = (color: ThemeColor) => {
    const newTheme = { ...theme, color };
    updateTheme(newTheme);
  };

  const value = {
    theme,
    setTheme: updateTheme,
    setMode,
    setColor,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
